import React from 'react';
import { Container, Row, Col } from 'reactstrap';
import { Link } from 'gatsby';

import Image from '../components/Image';
import Layout from '../components/Layout';

const AboutPage = () => (
  <Layout title="About">
    <Container>
      <section className="top-section section">
        <p className="font-weight-bold">
          We are at a turning point as a nation.
        </p>
        <p>
          Fear, isolation and misinformation have fractured communities and
          polarized our media. Many are looking for hope as we confront
          challenges that seem to continually divide us.
        </p>
        <p>
          LVN partners organize purposeful, boundary-crossing conversations
          where participants come to know one another through creative,
          constructive thinking and dialogue. The experience of being heard,
          asking questions, and engaging possibilities reignites their
          motivation to engage with thorny and complex problems.{' '}
        </p>
        <p>
          This practice of constructive conversation is humanizing, inviting us
          to understand our place in a more complex story. The opportunity to
          hear and engage with different views restores our trust in our
          neighbors, even when we continue to disagree.
        </p>
      </section>
    </Container>
    <section className="section section-bg bg-light-purple">
      <Container>
        <h2>Our Process</h2>
        <p>
          We work with each partner to identify the services and technology that
          will support their vision.
        </p>
        <Row className="d-flex align-items-center mb-4">
          <Col xs={12} md={6}>
            <Image
              image="/img/about-around-hearth.jpg"
              alt="a group of people sitting at a table around a hearth"
              className="section-image mb-3 mb-md-0"
            />
          </Col>
          <Col>
            <h3>Host conversations</h3>
            <p>
              Recruit participants for{' '}
              <a href="https://medium.com/cortico/how-to-build-community-through-virtual-conversations-cbf6e9ddd655">
                facilitated conversations
              </a>{' '}
              that draw out personal experiences and capture them via our cozy,
              app-assisted hardware or through Zoom.
            </p>

            <p>
              Make your own conversation guide. Check out our{' '}
              <a href="https://scripts.lvn.org">Script-o-matic</a>, a tool we’ve
              developed to support conversations on a variety of topics.{' '}
            </p>
          </Col>
        </Row>
        <Row className="d-flex align-items-center mb-4">
          <Col xs={12} className="mb-2">
            <h3>Explore conversation audio</h3>
            <p>
              Conversations are uploaded onto the LVN online platform, powered
              by
              <a href="https://cortico.ai"> Cortico</a> technology. Topics,
              auto-transcription, and search functionality allow participants to
              revisit and more deeply understand conversation content.
            </p>
            <p>
              LVN partners can choose to make their conversations public,
              opening up participant voices to community leaders, policymakers,
              members of the media, or geographically-separated communities and
              allies.
            </p>
            <p>
              Listen to a public conversation from our COVID-19 collection{' '}
              <a href="https://app.lvn.org/conversation/487">here</a>.
            </p>
          </Col>
          <Col>
            <Image
              image="/img/about-screenshot-conversation-detail.png"
              alt="a screenshot of the LVN website showing the details from a conversation in Mott Haven, NY"
              className="section-image rounded"
            />
          </Col>
        </Row>
        <Row className="mb-4">
          <Col>
            <h3 className="text-center">Surface key topics</h3>
            <p className="text-center">
              Get a sense of conversation themes with our automated topic
              generator.
            </p>
            <Image
              image="/img/about-screenshot-conversation-timeline.png"
              alt="a screenshot of the LVN website showing the timeline of a conversation with the topic homelessness selected"
              className="section-image rounded"
            />
          </Col>
        </Row>
        <Row className="d-flex align-items-center mb-4">
          <Col xs={12} md={6}>
            <h3>Search transcripts</h3>
            <p>
              Easily explore transcripts to find specific keywords and areas of
              interest.
            </p>
          </Col>
          <Col>
            <Image
              image="/img/about-screenshot-transcript-search.png"
              alt="a screenshot of the LVN website showing transcript search results for education"
              className="section-image rounded"
            />
          </Col>
        </Row>
        <Row className="d-flex align-items-center mb-4">
          <Col xs={{ size: 12, order: 1 }} md={{ size: 6, order: 0 }}>
            <Image
              image="/img/about-screenshot-finished-highlight.png"
              alt="a screenshot of the LVN website showing a highlight made from a conversation transcript"
              className="section-image rounded mb-3"
            />
          </Col>
          <Col className="mb-3">
            <h3>Share highlights</h3>
            <p>
              Identify meaningful conversation moments and share these excerpts
              with participants and the public.
            </p>
            <p>
              Browse some powerful examples of{' '}
              <Link to="/voices">conversation highlights</Link> across our
              collections.
            </p>
          </Col>
        </Row>
        <Row className="mb-4 text-center">
          <Col>
            <h3 className="text-center">
              Use collected conversations to tell a larger story
            </h3>
            <p>
              Selected highlights can be integrated into reports and
              presentations, or edited into a compilation that literally gives
              voice to unscripted perspectives.
            </p>
            <p>
              Read through a <Link to="/PFC">summary report</Link> and listen to
              an <a href="https://youtu.be/1xyFWkLjLT4">audio medley</a> for our
              recent partnership with the Madison Police and Fire Commission.
            </p>
          </Col>
        </Row>
      </Container>
    </section>
    <Row className="section section-bg bg-purple">
      <Col xs={12}>
        <div className="mx-auto text-center">
          <p className="mb-6">
            Want to learn more about how LVN can support your organization?
          </p>
          <Link to="/contact" className="btn btn-important btn-white">
            Contact Us
          </Link>
        </div>
      </Col>
    </Row>
  </Layout>
);

export default AboutPage;
